import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { SITE_CONFIG, SERVICES_FR, SERVICES_EN, CITIES } from '@/data/siteData';
import { Menu, X, Phone, ChevronDown, Globe } from 'lucide-react';

const Header = () => {
  const { lang, switchLanguage, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isCitiesOpen, setIsCitiesOpen] = useState(false);
  
  const services = lang === 'en' ? SERVICES_EN : SERVICES_FR;
  const prefix = lang === 'en' ? '/en' : '';

  return (
    <header className="bg-white border-b border-stone-100 sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to={`${prefix}/`} className="flex items-center gap-3" data-testid="header-logo">
            {/* Navy Blue Square with Gold VS */}
            <div className="w-12 h-12 bg-[#1a365d] rounded-lg flex items-center justify-center shadow-md">
              <span className="text-[#D4AF37] font-bold text-xl tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }}>VS</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-semibold text-[#1a365d] text-lg tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Riviera</span>
              <span className="text-[#D4AF37] font-medium ml-1.5 tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Conciergerie</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link 
              to={`${prefix}/`} 
              className="text-[#1a365d] hover:text-[#D4AF37] font-medium transition-colors tracking-wide"
            >
              {t('Accueil', 'Home')}
            </Link>
            
            {/* Services Dropdown */}
            <div className="relative group">
              <button 
                className="flex items-center gap-1 text-[#1a365d] hover:text-[#D4AF37] font-medium transition-colors tracking-wide"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                {t('Services', 'Services')}
                <ChevronDown className="w-4 h-4" />
              </button>
              <div 
                className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-stone-100 py-2 transition-all duration-200 ${isServicesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`${prefix}/services/${service.slug}`}
                    className="block px-4 py-2.5 text-[#1a365d] hover:bg-[#faf8f5] hover:text-[#D4AF37] transition-colors"
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            </div>

            {/* Cities Dropdown */}
            <div className="relative group">
              <button 
                className="flex items-center gap-1 text-[#1a365d] hover:text-[#D4AF37] font-medium transition-colors tracking-wide"
                onMouseEnter={() => setIsCitiesOpen(true)}
                onMouseLeave={() => setIsCitiesOpen(false)}
              >
                {t('Villes', 'Cities')}
                <ChevronDown className="w-4 h-4" />
              </button>
              <div 
                className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-stone-100 py-2 transition-all duration-200 ${isCitiesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                onMouseEnter={() => setIsCitiesOpen(true)}
                onMouseLeave={() => setIsCitiesOpen(false)}
              >
                {CITIES.map((city) => (
                  <Link
                    key={city.id}
                    to={lang === 'en' 
                      ? `${prefix}/apartment-cleaning-${city.slug}`
                      : `/nettoyage-appartement-${city.slug}`
                    }
                    className="block px-4 py-2.5 text-[#1a365d] hover:bg-[#faf8f5] hover:text-[#D4AF37] transition-colors"
                  >
                    {lang === 'en' ? city.nameEn : city.nameFr}
                  </Link>
                ))}
              </div>
            </div>

            <Link 
              to={`${prefix}/blog`} 
              className="text-[#1a365d] hover:text-[#D4AF37] font-medium transition-colors tracking-wide"
            >
              Blog
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <button
              onClick={() => switchLanguage(lang === 'fr' ? 'en' : 'fr')}
              className="flex items-center gap-1.5 text-[#1a365d] hover:text-[#D4AF37] transition-colors"
              data-testid="language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase tracking-wider">{lang === 'fr' ? 'EN' : 'FR'}</span>
            </button>

            {/* Phone CTA - Gold Style */}
            <a
              href={SITE_CONFIG.phoneLink}
              className="hidden md:flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#C9A227] hover:to-[#B8960C] text-white px-5 py-2.5 rounded-full font-medium transition-all shadow-md hover:shadow-lg"
              data-testid="header-phone-cta"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">{SITE_CONFIG.phone}</span>
              <span className="xl:hidden">{t('Appeler', 'Call')}</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-[#1a365d] hover:text-[#D4AF37]"
              data-testid="mobile-menu-button"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-stone-100 py-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <Link 
                to={`${prefix}/`} 
                className="text-[#1a365d] hover:text-[#D4AF37] font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('Accueil', 'Home')}
              </Link>
              
              <div>
                <span className="text-[#D4AF37] text-sm uppercase tracking-wider font-medium">{t('Services', 'Services')}</span>
                <div className="mt-2 ml-4 flex flex-col gap-2">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`${prefix}/services/${service.slug}`}
                      className="text-[#1a365d] hover:text-[#D4AF37] py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-[#D4AF37] text-sm uppercase tracking-wider font-medium">{t('Villes', 'Cities')}</span>
                <div className="mt-2 ml-4 flex flex-col gap-2">
                  {CITIES.map((city) => (
                    <Link
                      key={city.id}
                      to={lang === 'en' 
                        ? `${prefix}/apartment-cleaning-${city.slug}`
                        : `/nettoyage-appartement-${city.slug}`
                      }
                      className="text-[#1a365d] hover:text-[#D4AF37] py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {lang === 'en' ? city.nameEn : city.nameFr}
                    </Link>
                  ))}
                </div>
              </div>

              <Link 
                to={`${prefix}/blog`} 
                className="text-[#1a365d] hover:text-[#D4AF37] font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>

              <a
                href={SITE_CONFIG.phoneLink}
                className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] text-white px-4 py-3 rounded-lg font-medium justify-center mt-4 shadow-md"
              >
                <Phone className="w-4 h-4" />
                {SITE_CONFIG.phone}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
